import { ReactElement } from 'react';
import { formatTranslation } from 'intl';
import { FiltersTranslationKeys } from 'intl/keys';
import SaveIcon from '@mui/icons-material/Save';
import SaveAsIcon from '@mui/icons-material/SaveAs';
import UndoIcon from '@mui/icons-material/Undo';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import {
  filterPanelFormsHeaderSx,
  filterPanelFormsHeaderTitleSx,
  filterPanelFormsHeaderActionsSx,
} from '../styles';
import { FilterPresetListItem } from '../types';

type FilterPanelFormsHeaderProps = {
  activePreset: FilterPresetListItem | undefined;
  isModified: boolean;
  hasUnsavedFilters: boolean;
  onOpenCreateDialog: () => void;
  onSaveChanges: () => void;
  onDiscardChanges: () => void;
};

export function FilterPanelFormsHeader({
  activePreset,
  isModified,
  hasUnsavedFilters,
  onOpenCreateDialog,
  onSaveChanges,
  onDiscardChanges,
}: FilterPanelFormsHeaderProps): ReactElement {
  const intl = useIntl();

  const showSaveAsNew = hasUnsavedFilters || (isModified && !activePreset?.isStatic);
  const showSaveDiscard = isModified && activePreset && !activePreset.isStatic;

  const getTitle = (): string => {
    if (activePreset) {
      return activePreset.name;
    }
    return formatTranslation(intl, FiltersTranslationKeys.currentPreset);
  };

  return (
    <Box sx={filterPanelFormsHeaderSx}>
      <Box sx={filterPanelFormsHeaderTitleSx}>
        <Typography variant='subtitle2' noWrap>
          {getTitle()}
        </Typography>

        {activePreset?.isStatic && (
          <Chip
            label={formatTranslation(intl, FiltersTranslationKeys.presetPredefined)}
            size='small'
            variant='outlined'
            color='default'
          />
        )}

        {isModified && !activePreset?.isStatic && (
          <Chip
            label={formatTranslation(intl, FiltersTranslationKeys.unsavedChanges)}
            size='medium'
            variant='outlined'
            color='warning'
          />
        )}
      </Box>

      <Box sx={filterPanelFormsHeaderActionsSx}>
        {showSaveDiscard && (
          <>
            <Tooltip title={formatTranslation(intl, FiltersTranslationKeys.saveChanges)}>
              <IconButton
                size='small'
                onClick={onSaveChanges}
                aria-label='save-changes'
                color='primary'
              >
                <SaveIcon fontSize='small' />
              </IconButton>
            </Tooltip>
            <Tooltip title={formatTranslation(intl, FiltersTranslationKeys.discardChanges)}>
              <IconButton
                size='small'
                onClick={onDiscardChanges}
                aria-label='discard-changes'
              >
                <UndoIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </>
        )}

        {showSaveAsNew && (
          <Tooltip title={formatTranslation(intl, FiltersTranslationKeys.saveAsNew)}>
            <IconButton
              size='small'
              onClick={onOpenCreateDialog}
              aria-label='save-as-new'
              color='primary'
            >
              <SaveAsIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}
